import type {
  AttributeSpec,
  AttributeSpecValidateResult,
  AttributeValue,
} from "../../types/index.js";
import { REGEX_ASCII_WHITESPACE } from "../../shared/index.js";
import { valid, invalid } from "../../shared/result.js";
import { ERROR_MESSAGES } from "./error-messages.js";

export type SpaceSeparatedTokensOptions = {
  unique: boolean;
  allowed?: string[];
  validateToken?: (value: string) => boolean;
};

/**
 * @see https://html.spec.whatwg.org/multipage/common-microsyntaxes.html#space-separated-tokens
 */
export class SpaceSeparatedTokens implements AttributeSpec {
  static type = "SpaceSeparatedTokens" as const;
  constructor(private options: SpaceSeparatedTokensOptions) {}

  private parse(value: string) {
    const tokens = value.split(REGEX_ASCII_WHITESPACE);
    return tokens;
  }

  validateValue(value: AttributeValue): AttributeSpecValidateResult {
    const tokens = this.parse(value).filter((token) => token !== "");

    // Check uniqueness
    if (this.options.unique) {
      const uniqueTokens = new Set(tokens);

      if (tokens.length !== uniqueTokens.size) {
        return invalid("Tokens must be unique");
      }
    }

    // Check allowed tokens
    if (this.options.allowed) {
      for (const token of tokens) {
        const isAllowed = this.options.allowed.some((allowedToken) => {
          return token.toLowerCase() === allowedToken.toLowerCase();
        });

        if (!isAllowed) {
          return invalid(
            `Invalid token: "${token}". Allowed tokens: ${this.options.allowed.join(", ")}`,
          );
        }
      }
    }
    // Custom token validation
    if (typeof this.options.validateToken === "function") {
      for (const token of tokens) {
        if (!this.options.validateToken(token)) {
          return invalid(`Invalid token: "${token}"`);
        }
      }
    }

    return valid();
  }
}
